import { useNode } from "@craftjs/core";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { ColumnBlockSettings } from "@/components/craft/settings/ColumnBlockSettings";

const LAYOUT_BLOCKS = new Set(["SectionBlock", "RowBlock", "ColumnBlock"]);

type ColumnBlockProps = {
  widthPercent?: number;
  style?: ResponsiveStyleProps;
  children?: ReactNode;
};

export function ColumnBlock({
  widthPercent = 100,
  style = defaultStyleProps(),
  children,
}: ColumnBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        "craft-node min-w-0",
        `craft-node-${id}`,
        styleClass,
        selected && "outline outline-2 outline-dashed outline-primary/20",
      )}
      style={{
        ...inlineStyle,
        flex: `1 1 ${widthPercent}%`,
        maxWidth: `${widthPercent}%`,
      }}
    >
      {children}
    </div>
  );
}

ColumnBlock.craft = {
  displayName: "Column",
  isCanvas: true,
  props: {
    widthPercent: 100,
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
    canMoveIn: (incoming) =>
      !incoming.some((node) => LAYOUT_BLOCKS.has(node.data.name ?? "")),
  },
  related: {
    settings: ColumnBlockSettings,
  },
};
